import { useMemo } from 'react'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { Text, Popup } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { TypographyStyles } from '@/styles'
import { TransactionsByCategoryId } from '@/v2-features/transactions/transactions.slice'

const LineItemTransaction = ({
  transaction,
  showManualUploadTooltip = false,
}: {
  transaction: TransactionsByCategoryId['transactions'][number]
  showManualUploadTooltip?: boolean
}) => {
  const title = useMemo(
    () => (
      <Text variant={TypographyStyles.BODY_SM} className="mr-4">
        {isoToUTCDateTime(transaction?.date ?? '').toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT_NO_PAD
        )}
      </Text>
    ),
    [transaction.date]
  )

  const amount = useMemo(
    () => (
      <Text
        variant={TypographyStyles.BODY_SM}
        className="text-left md:text-end"
      >
        <CurrencyFormatLabel
          value={centsToDollars(transaction?.amountInCents)}
        />
      </Text>
    ),
    [transaction.amountInCents]
  )

  const description = useMemo(
    () => (
      <Text variant={TypographyStyles.BODY_SM} className="w-full">
        &nbsp;{transaction?.description || 'N/A'}
      </Text>
    ),
    [transaction.description]
  )

  return (
    <div className="flex flex-col md:flex-row flex-wrap border-b border-neutral-200 pb-2">
      <div className="w-full md:w-3/12 text-left">{title}</div>
      <div className="w-full md:w-6/12 text-left flex items-center gap-2">
        {description}
        {showManualUploadTooltip && (
          <Popup
            content={
              <div className="flex flex-col gap-2">
                <Text variant={TypographyStyles.LABEL_MD}>
                  Why is this transaction shown twice?
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  User manual upload transactions are displayed both under their
                  original category and under Owner&apos;s Investments or
                  Owner&apos;s Distributions. This accounts for personal funds
                  being used for these transactions.
                </Text>
              </div>
            }
            side="top"
            maxWidth="max-w-sm"
          />
        )}
      </div>
      <div className="w-full md:w-3/12 text-left">{amount}</div>
    </div>
  )
}

export { LineItemTransaction }
